import { useEffect, useState } from "react";

import { useParams, useSearchParams } from "react-router-dom";

import { TAccount, TCourse, TSummary } from "../../../../@types";
import { apis, callApi } from "../../../../api";

export type TLearningService = {
	listeners: (() => void)[];
	loading: boolean;
	summary: TSummary | null;
	course: TCourse | null;
	teacher: TAccount | null;

	addEventListener: (cb: () => void) => void;
	clearData: () => void;
	emitChanges: () => void;
	removeEventListener: (cb: () => void) => void;

	setLoading: (loading: boolean) => void;
	setSummary: (summary: TSummary) => void;
	setCourse: (course: TCourse) => void;
	setTeacher: (teacher: TAccount) => void;

	updateStudentProgress: (
		bookingId: number,
		bookingData: {
			airHours: number;
			simulatorHours: number;
			milesFlown: number;
			flightSummary: string;
		},
		summaryData: {
			airHours: number;
			simulatorHours: number;
			milesFlown: number;
			flightSummary: string;
		},
	) => void;
};

export const LearningService: TLearningService = {
	listeners: [],
	loading: false,
	summary: null,
	course: null,
	teacher: null,

	addEventListener(cb) {
		this.listeners = this.listeners.filter((listener) => listener !== cb);
		this.listeners.push(cb);
	},

	clearData() {
		this.loading = false;
		this.summary = null;
	},

	emitChanges() {
		for (const listener of this.listeners) {
			listener();
		}
	},

	removeEventListener(cb) {
		this.listeners = this.listeners.filter((listener) => listener !== cb);
	},

	setLoading(loading) {
		this.loading = loading;
	},

	setSummary(summary) {
		this.summary = summary;
	},

	setCourse(course) {
		this.course = course;
	},

	setTeacher(teacher) {
		this.teacher = teacher;
	},

	updateStudentProgress(bookingId, bookingData, summaryData) {
		if (!this.summary) {
			return;
		}

		const index = this.summary.bookings.findIndex(
			(booking) => booking.id === bookingId,
		);

		if (index === -1) {
			return;
		}

		this.summary.bookings[index] = {
			...this.summary.bookings[index],
			...bookingData,
		};
		this.summary = { ...this.summary, ...summaryData };
	},
};

export function useLearningInit() {
	const { courseSlug } = useParams();
	const [searchParams, setSearchParams] = useSearchParams();

	useEffect(() => {
		const getData = async () => {
			try {
				LearningService.setLoading(true);
				LearningService.emitChanges();

				const response = await callApi({
					method: "GET",
					url: `${apis.SUMMARY_BY_STUDENT_TOKEN}/${courseSlug}`,
				});

				if (response?.summary) {
					LearningService.setSummary(response.summary);
					const classId = searchParams.get("class");
					if (classId === null && response.summary.bookings.length > 0) {
						setSearchParams(
							(prev) => {
								prev.set("class", response.summary.bookings[0].id.toString());
								return prev;
							},
							{ replace: true },
						);
					}
				}
				if (response?.course) {
					LearningService.setCourse(response.course);
				}
				if (response?.teacher) {
					LearningService.setTeacher(response.teacher);
				}
			} catch (error) {
			} finally {
				LearningService.setLoading(false);
				LearningService.emitChanges();
			}
		};

		getData();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [courseSlug]);

	useEffect(() => {
		return () => {
			LearningService.clearData();
		};
	}, []);
}

export function useLearning() {
	const [loading, setLoading] = useState(LearningService.loading);
	const [summary, setSummary] = useState(LearningService.summary);
	const [course, setCourse] = useState(LearningService.course);
	const [teacher, setTeacher] = useState(LearningService.teacher);
	const [searchParams] = useSearchParams();

	const classId = Number(searchParams.get("class") ?? "0");
	const currentBooking = summary?.bookings.find(
		(booking) => booking.id === classId,
	);

	useEffect(() => {
		const updateData = () => {
			setLoading(LearningService.loading);
			setSummary(LearningService.summary);
			setCourse(LearningService.course);
			setTeacher(LearningService.teacher);
		};

		LearningService.addEventListener(updateData);

		return () => {
			LearningService.removeEventListener(updateData);
		};
	}, []);

	return {
		loading,
		summary,
		course,
		teacher,
		currentBooking,
	};
}
